import CardRemove from "@assets/icons/card-frozen-v2.svg";
import MoneyChange from "@assets/icons/money-change.svg";
import { Block } from "@components/block";
import { Breadcrumbs } from "@components/breadcrumbs";
import { FlexLoader } from "@components/loader";
import { RequestModal } from "@components/request-modal";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { Config, DoubleText, Table } from "@components/table";
import { useAppeals } from "@features/appeals";
import { useBalance } from "@features/balance";
import { formatDate, formatTime } from "@helpers/date";
import { formatRubles, formatUsdt } from "@helpers/format";
import { usePagination } from "@hooks/use-pagination";
import { useCallback, useEffect, useState } from "react";
import styles from "./disputes.module.scss";
import { EllipsisText } from "@components/ellipsis-text";
import { MergedAppeal } from "@features/appeals/slice";
import { useWebSocket } from "@request/useWebsocket";
import { WebsocketUpdateType } from "@request/websocket";

export const traderAppelConfig: Config<MergedAppeal> = [
  {
    label: "Номер заявки",
    source: "order_uuid",
    renderComponent: (item) => <EllipsisText text={item} copy />,
  },

  {
    label: "Дата и время",
    source: "created_at",
    renderComponent: (dateTime) => {
      const time = formatTime(new Date(dateTime));
      const date = formatDate(new Date(dateTime));
      return <DoubleText text={time} subText={date} />;
    },
  },
  {
    label: "Сумма заявки",
    source: "order_start_amount",
    renderComponent: (cell, item) => {
      if (cell == null || item.order_start_amount_usdt == null) return "";
      const rub = formatRubles(cell);
      const usdt = formatUsdt(item.order_start_amount_usdt);
      return <DoubleText text={rub} subText={usdt} />;
    },
  },
  {
    label: "Сумма мерчанта",
    source: "merchant_amount",
    renderComponent: (cell, item) => {
      if (cell == null || item.merchant_amount_usdt == null) return "";
      const rub = formatRubles(cell);
      const usdt = formatUsdt(item.merchant_amount_usdt);
      return <DoubleText text={rub} subText={usdt} />;
    },
  },
  {
    label: "Итоговая сумма",
    source: "order_final_amount",
    renderComponent: (cell, item) => {
      if (cell == null || item.order_final_amount_usdt == null) return "";
      const rub = formatRubles(cell);
      const usdt = formatUsdt(item.order_final_amount_usdt);
      return <DoubleText text={rub} subText={usdt} />;
    },
  },
  { label: "Устройство", source: "device_name" },
  {
    label: "Тип",
    source: "order_type",
    renderComponent: (order_type) => {
      switch (order_type) {
        case "pay_out":
          return "Вывод";
        case "pay_in":
          return "Сделка";
        default:
          return "Не определено";
      }
    },
  },
  {
    label: "Статус",
    source: "status",
    renderComponent: (status) => {
      let text = "";
      let statusType: StatusBlockType;
      switch (status) {
        case "canceled":
          text = "Отклонен";
          statusType = StatusBlockType.Error;
          break;
        case "approved":
          text = "Пересмотрен";
          statusType = StatusBlockType.Success;
          break;
        case "processing":
        case "pending_support":
        default:
          text = "На рассмотрении";
          statusType = StatusBlockType.Warning;
          break;
      }
      return <StatusBlock text={text} type={statusType} />;
    },
  },
];

export const DisputesPage = () => {
  const { state } = useBalance();

  if (state.errorSave) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Breadcrumbs items={["Замороженный баланс", "Все споры"]} />
      <div className={styles.balanceContainer}>
        <Balance
          title="Замороженный баланс"
          icon={CardRemove}
          balance={state.frozen}
          loading={state.loadingSave}
        />
        <Balance
          title="Страховой депозит"
          icon={MoneyChange}
          balance={state.save}
          loading={state.loadingSave}
        />
      </div>
      <Appeals />
    </div>
  );
};

type BalanceProps = {
  title: string;
  balance?: number | null;
  loading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any;
};

const Balance = ({ balance, icon: Icon, title, loading }: BalanceProps) => {
  return (
    <Block className={styles.balanceBlock}>
      <div>
        <h3 className={styles.balanceTitle}>{title}</h3>
        {loading && !balance ? (
          <FlexLoader />
        ) : (
          <div className={styles.balance}>{formatUsdt(balance || 0)}</div>
        )}
      </div>
      <div className={styles.icon}>
        <Icon />
      </div>
    </Block>
  );
};

const Appeals = () => {
  const { state, getAppeals } = useAppeals();
  const { page, limit, setPage, setLimit } = usePagination(100);
  const { lastMessage } = useWebSocket();

  useEffect(() => {
    if (lastMessage && lastMessage.type == WebsocketUpdateType.Appeal) {
      getAppeals({ limit, page });
    }
  }, [lastMessage, limit, page]);

  useEffect(() => {
    getAppeals({ page, limit });
  }, [getAppeals, limit, page]);

  const [selectedAppeal, setSelectedAppeal] = useState<MergedAppeal>();

  const hideModal = useCallback(() => {
    setSelectedAppeal(undefined);
  }, []);

  return (
    <>
      <Block>
        <Table
          title="Все споры"
          onRowClick={setSelectedAppeal}
          data={state.appeals}
          loading={state.loading}
          config={traderAppelConfig}
          setPage={setPage}
          setLimit={setLimit}
          page={page}
          limit={limit}
          limits={[100, 200, 300]}
          totalCount={state.totalCount}
        />
      </Block>
      <RequestModal appeal={selectedAppeal} onClose={hideModal} />
    </>
  );
};
